import React, { FC } from 'react';
import PropTypes from 'prop-types';
import { AppBar, AppBarProps, Box, Button, Divider, IconButton, Toolbar, Tooltip, Stack, Typography } from '@mui/material';
import MenuIcon from '../../icons/Menu';
import AccountPopover from './AccountPopover';
import PayLogo from '../../logos/PayLogo';
import { remoteRoutes } from '../../constants';
import MessagingLogo from '../../logos/MessagingLogo';
import { useTheme } from '@mui/material/styles';
import VerifyLogo from "../../logos/VerifyLogo";
import { ThemePopover } from './ThemePopover';
import useAuth from "../../hooks/useAuth";
import { getFirstName } from "../../utils/userHelper";
import { SIDEBAR_WIDTH } from '../../constants';

interface DashboardNavbarProps extends AppBarProps {
  onSidebarMobileOpen?: () => void;
}

const products = [
  {
    name: 'Pay',
    logo: <PayLogo sx={{ width: 30, height: 30, }}/>,
    url: remoteRoutes.pay
  },
  {
    name: 'Verify',
    logo: <VerifyLogo sx={{ width: 30, height: 30, }}/>,
    url: remoteRoutes.verify
  },
  {
    name: 'Messaging',
    logo: <MessagingLogo sx={{ width: 30, height: 30, }}/>,
    url: remoteRoutes.messaging
  },
];

const DashboardNavbar: FC<React.PropsWithChildren<DashboardNavbarProps>> = (props) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { onSidebarMobileOpen, ...other } = props;

  return (
        <AppBar
            {...other}
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderBottom: `1px solid ${theme.palette.divider}`,
              width: { lg: `calc(100% - ${SIDEBAR_WIDTH}px)` },
              ml: { lg: `${SIDEBAR_WIDTH}px` }
            }}
        >
            <Toolbar>
                <IconButton
                    color="inherit"
                    size="large"
                    sx={{
                      display: {
                        lg: 'none',
                        md: 'block'
                      }
                    }}
                    onClick={onSidebarMobileOpen}
                >
                    <MenuIcon
                        fontSize="small"
                        color="action"
                    />
                </IconButton>
              <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    flexGrow: 1,
                    ml: {
                      xs: 0,
                      sm: 0,
                      md: 0,
                      lg: 2,
                    },
                    display: {
                      xs: 'none',
                      sm: 'none',
                      md: 'flex',
                      lg: 'flex',
                    },
                    alignItems: 'center',
                    height: '100%',
                  }}
              >
                <img
                    src="/assets/emoji_wave.svg"
                    alt="Wave"
                />
                <Typography
                    color="textPrimary"
                    variant="h6"
                >
                  Hello, {getFirstName(user?.name)}
                </Typography>
              </Stack>
                <Box sx={{ flexGrow: 1 }} />
                <Box
                    sx={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      borderRadius: 1,
                      py: 0.4,
                      backgroundColor: theme.palette.background.default,
                    }}
                >
                    {
                        products.map((product, index) => (
                            <Tooltip
                                key={index}
                                title={product.name}
                            >
                                <Button
                                    href={product.url}
                                    target="_blank"
                                >
                                    {product.logo}
                                </Button>
                            </Tooltip>
                        ))
                    }
                </Box>
                <Box
                    sx={{
                      ml: { xs: 0, sm: 2 }
                    }}
                >
                    <ThemePopover />
                </Box>
                <Divider sx={{ height: 40, mx: 2, width: 2, backgroundColor: theme.palette.divider }}/>
                <Box>
                    <AccountPopover/>
                </Box>
            </Toolbar>
        </AppBar>
  );
};

DashboardNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func
};

export default DashboardNavbar;
